/**
 * Shared Discovery header/footer view types and singleton ids. Lives here
 * rather than in `@repo/ui` because the mappers in this package need them and
 * `@repo/ui` already depends on `@repo/sanity-utils`.
 */
/** Fixed singleton document IDs (must match apps/sanity-studio/config/documentIds.ts). */
export const DISCOVERY_HEADER_ID = "281e44a6-5baa-4f07-92f5-2194d6bf2fe3";
export const DISCOVERY_FOOTER_ID = "b699f5da-d8f4-4981-9d47-0b34fac19d83";

export type DiscoveryNavLinkView = {
	_key: string;
	label: string;
	description?: string;
	href: string;
	openInNewTab?: boolean;
	iconUrl?: string;
};

export type DiscoveryTopLinkView = {
	_key: string;
	label: string;
	href: string;
	openInNewTab?: boolean;
	iconUrl?: string;
	isCurrent?: boolean;
};

export type DiscoveryNavItemView = {
	_key: string;
	label: string;
	href?: string;
	openInNewTab?: boolean;
	panelGroupLabel?: string;
	children: DiscoveryNavLinkView[];
};

export type DiscoveryHeaderView = {
	logoUrl?: string;
	logoHref?: string;
	logoAlt?: string;
	topLinks: DiscoveryTopLinkView[];
	items: DiscoveryNavItemView[];
};

export type DiscoverySocialLinkView = {
	_key: string;
	key: string;
	label: string;
	url: string;
};

export type DiscoveryFooterView = {
	showLogo: boolean;
	logoUrl?: string;
	logoHref?: string;
	logoAlt?: string;
	style: "chalkboardGreen" | "subtlePaper";
	items: DiscoveryNavItemView[];
	socialLinks: DiscoverySocialLinkView[];
	copyrightText: string;
	legalLinks: DiscoveryNavLinkView[];
};

export function linkTargetProps(openInNewTab?: boolean) {
	if (!openInNewTab) return {};
	return {target: "_blank" as const, rel: "noopener noreferrer"};
}

/**
 * Footer/header "href" values may be a normal URL/path or a CMS-authored JS
 * command (e.g. Osano cookie preferences drawer).
 */
export function isDiscoveryJsCommandHref(href: string): boolean {
	const value = href.trim();
	if (!value) return false;
	if (/^javascript:/i.test(value)) return true;
	return /^(window|Osano)\b/.test(value);
}

/**
 * Runs a CMS-authored Discovery chrome JS command. Call only from client
 * click handlers — values come from trusted Studio editors.
 */
export function runDiscoveryJsCommand(href: string): void {
	const code = href.trim().replace(/^javascript:/i, "");
	if (!code) return;
	try {
		const run = new Function(code);
		run();
	} catch (error) {
		console.error("[DiscoveryChrome] JS command failed:", error);
	}
}
